import { Component, OnInit } from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ApiService } from '../../../shared/services/api.service';
import {
  CreateSchedulerRecordInput,
  CreateSchedulerRecordMutation,
  CreateSchedulerRecordMutationVariables,
  GetQueryRecordsQuery,
  GetQueryRecordsQueryVariables,
  QueryRecord,
  QueryType,
  ScheduleType,
  SchedulerStatus,
} from '../../../../API';
import { createSchedulerRecord } from '../../../../graphql/mutations';
import { ScheduleEndInput } from '../../../../API';
import { EndType } from '../../../../API';
import { RecurrenceFrequency } from '../../../../API';
import { DayOfWeek } from '../../../../API';
import { MonthlyRecurrenceInput } from '../../../../API';
import { MonthlyMode } from '../../../../API';
import { WeekOfMonth } from '../../../../API';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GetQueryRecords } from '../../../../graphql/queries';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS } from '../../../shared/utils/constants';
import dayjs from 'dayjs';

@Component({
  selector: 'app-create-schedule-modal',
  templateUrl: './create-schedule-modal.component.html',
  styleUrl: './create-schedule-modal.component.scss',
})
export class CreateScheduleModalComponent implements OnInit {
  queryDescription: string | null | undefined = '';

  selectQueryOptions: { name: string; value: string }[] = [];

  readonly selectTimezoneOptions = [
    { name: 'EST', value: 'est' },
    { name: 'HST', value: 'hst' },
    { name: 'AKDT', value: 'akdt' },
    { name: 'PST', value: 'pst' },
    { name: 'MST', value: 'mst' },
    { name: 'CST', value: 'cst' },
    { name: 'AST', value: 'ast' },
    { name: 'NST', value: 'nst' },
  ];

  schedulerName = '';
  selectedQuery: string | null = null;
  selectedTimezone: string | null = null;
  selectedTabIndex = 0;
  tabValues = [ScheduleType.ONE_TIME, ScheduleType.RECURRING];
  selectedTabValue = this.tabValues[0];
  oneTimeDate: Date | null = null;
  oneTimeTime: Date | null = null;
  recurrenceFrequency = RecurrenceFrequency.DAILY;
  recurringTime: Date | null = null;
  recurringDate: Date | null = null;
  interval = 2;
  showInputControls = false;
  endType = EndType.NEVER;
  repeatType: string | null = null;
  dateOfMonth: string | null = null;
  dayOfWeek: DayOfWeek | null = null;
  weekOfMonth: WeekOfMonth | null = null;
  selectedDayOfWeek: DayOfWeek[] = [];
  monthlyRepeatModes: string | null = null;
  endTypeOnDate: Date | null = null;
  endTypeAfter: number | null = null;
  isLoading = false;
  queryRows: QueryRecord[] = [];
  selectedQueryRecord: QueryRecord = {} as QueryRecord;

  queryName: string | null | undefined = '';
  queryId: string | null = '';
  queryRaw: string | null | undefined = '';

  schedulerForm!: FormGroup;
  summary = '';
  today = new Date();

  readonly weekOfMonthOption = [
    { name: 'First', value: WeekOfMonth.FIRST },
    { name: 'Second', value: WeekOfMonth.SECOND },
    { name: 'Third', value: WeekOfMonth.THIRD },
    { name: 'Fourth', value: WeekOfMonth.FOURTH },
    { name: 'Last', value: WeekOfMonth.LAST },
  ];

  readonly dayOfWeekOption = [
    { name: 'Sunday', value: DayOfWeek.SUNDAY },
    { name: 'Monday', value: DayOfWeek.MONDAY },
    { name: 'Tuesday', value: DayOfWeek.TUESDAY },
    { name: 'Wednesday', value: DayOfWeek.WEDNESDAY },
    { name: 'Thursday', value: DayOfWeek.THURSDAY },
    { name: 'Friday', value: DayOfWeek.FRIDAY },
    { name: 'Saturday', value: DayOfWeek.SATURDAY },
  ];

  private dayIndexMap: Record<DayOfWeek, number> = {
    [DayOfWeek.SUNDAY]: 0,
    [DayOfWeek.MONDAY]: 1,
    [DayOfWeek.TUESDAY]: 2,
    [DayOfWeek.WEDNESDAY]: 3,
    [DayOfWeek.THURSDAY]: 4,
    [DayOfWeek.FRIDAY]: 5,
    [DayOfWeek.SATURDAY]: 6,
  };

  constructor(
    private nzModalRef: NzModalRef,
    private apiService: ApiService,
    private userManagementService: UserManagementService,
    private nzMessageService: NzMessageService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.getQueryDropDown();
    this.schedulerForm = this.buildBaseForm();

    // Default tab
    this.setOneTimeControls();

    this.schedulerForm.get('scheduleType')!.valueChanges.subscribe((type) => {
      if (type === 'ONE_TIME') {
        this.setOneTimeControls();
      } else {
        this.setRecurringControls();
      }
    });

    this.schedulerForm.valueChanges.subscribe(() => {
      this.updateSummary();
    });
  }
  buildBaseForm(): FormGroup {
    return this.fb.group({
      schedulerName: [
        '',
        [
          Validators.required,
          Validators.maxLength(50),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      selectedQuery: [null, Validators.required],
      selectedTimezone: [null, Validators.required],

      scheduleType: [ScheduleType.ONE_TIME], // TAB DRIVER
      schedule: this.fb.group({}), // DYNAMIC
    });
  }
  setOneTimeControls(): void {
    const oneTimeGroup = this.fb.group({
      date: [null, Validators.required],
      time: [null, Validators.required],
    });

    this.schedulerForm.setControl('schedule', oneTimeGroup);
  }

  setRecurringControls(): void {
    const recurringGroup = this.fb.group({
      recurrenceFrequency: [RecurrenceFrequency.DAILY],
      recurrence: this.fb.group({}),
    });

    this.schedulerForm.setControl('schedule', recurringGroup);

    this.setRecurrenceFrequencyControls(RecurrenceFrequency.DAILY);

    recurringGroup.get('recurrenceFrequency')!.valueChanges.subscribe((freq) => {
      if (
        freq === RecurrenceFrequency.DAILY ||
        freq === RecurrenceFrequency.WEEKLY ||
        freq === RecurrenceFrequency.MONTHLY
      )
        this.setRecurrenceFrequencyControls(freq);
    });
  }

  setRecurrenceFrequencyControls(freq: 'DAILY' | 'WEEKLY' | 'MONTHLY'): void {
    let recurrenceGroup: FormGroup;

    if (freq === RecurrenceFrequency.DAILY) {
      recurrenceGroup = this.fb.group({
        interval: [1, [Validators.required, Validators.min(1)]],
        time: [null, Validators.required],
        startDate: [null, Validators.required],
        endType: [EndType.NEVER],
        endDate: [{ value: null, disabled: true }, Validators.required],
        occurrences: [{ value: null, disabled: true }, Validators.required],
      });
    }

    if (freq === RecurrenceFrequency.WEEKLY) {
      recurrenceGroup = this.fb.group({
        interval: [1, Validators.required],
        daysOfWeek: this.fb.array([], Validators.required),
        time: [null, Validators.required],
        startDate: [null, Validators.required],
        endType: [EndType.NEVER],
        endDate: [{ value: null, disabled: true }, Validators.required],
        occurrences: [{ value: null, disabled: true }, Validators.required],
      });
    }

    if (freq === RecurrenceFrequency.MONTHLY) {
      recurrenceGroup = this.fb.group({
        interval: [1, Validators.required],
        repeatType: [null],
        dateOfMonth: [null],
        weekOfMonth: [null],
        dayOfWeek: [null],
        time: [null, Validators.required],
        startDate: [null, Validators.required],
        endType: [EndType.NEVER],
        endDate: [{ value: null, disabled: true }, Validators.required],
        occurrences: [{ value: null, disabled: true }, Validators.required],
      });
    }

    (this.schedulerForm.get('schedule') as FormGroup).setControl('recurrence', recurrenceGroup!);

    this.handleEndTypeChanges();
  }

  get daysOfWeekArray(): FormArray {
    return this.schedulerForm.get('schedule.recurrence.daysOfWeek') as FormArray;
  }

  handleEndTypeChanges(): void {
    const recurrence = this.schedulerForm.get('schedule.recurrence') as FormGroup;
    if (!recurrence) return;

    const endTypeCtrl = recurrence.get('endType');
    const endDateCtrl = recurrence.get('endDate');
    const occurrencesCtrl = recurrence.get('occurrences');

    const monthlyRepeatTypeCtrl = recurrence.get('repeatType');
    const monthlyDateOfMonthCtrl = recurrence.get('dateOfMonth');
    const monthlyWeekOfMonthCtrl = recurrence.get('weekOfMonth');
    const monthlyDayOfMonthCtrl = recurrence.get('dayOfWeek');

    // Initial state
    this.toggleEndControls(endTypeCtrl?.value, endDateCtrl, occurrencesCtrl);

    endTypeCtrl?.valueChanges.subscribe((endType) => {
      this.toggleEndControls(endType, endDateCtrl, occurrencesCtrl);
    });

    this.toggleMonthlyRepeatControls(
      monthlyRepeatTypeCtrl?.value,
      monthlyDateOfMonthCtrl,
      monthlyWeekOfMonthCtrl,
      monthlyDayOfMonthCtrl,
    );
    monthlyRepeatTypeCtrl?.valueChanges.subscribe((repeatType) => {
      this.toggleMonthlyRepeatControls(
        repeatType,
        monthlyDateOfMonthCtrl,
        monthlyWeekOfMonthCtrl,
        monthlyDayOfMonthCtrl,
      );
    });
  }

  private toggleEndControls(
    endType: 'NEVER' | 'ON_DATE' | 'AFTER_OCCURRENCES',
    endDateCtrl: AbstractControl | null,
    occurrencesCtrl: AbstractControl | null,
  ): void {
    endDateCtrl?.disable();
    occurrencesCtrl?.disable();

    if (endType === EndType.ON_DATE) {
      endDateCtrl?.enable();
    }

    if (endType === EndType.AFTER_OCCURRENCES) {
      occurrencesCtrl?.enable();
    }
  }

  private toggleMonthlyRepeatControls(
    monthlyRepeatType: 'on-day' | 'on-the',
    monthlyDateOfMonthCtrl: AbstractControl | null,
    monthlyWeekOfMonthCtrl: AbstractControl | null,
    monthlyDayOfMonthCtrl: AbstractControl | null,
  ): void {
    monthlyDateOfMonthCtrl?.disable();
    monthlyWeekOfMonthCtrl?.disable();
    monthlyDayOfMonthCtrl?.disable();

    if (monthlyRepeatType === 'on-day') {
      monthlyDateOfMonthCtrl?.enable();
    }

    if (monthlyRepeatType === 'on-the') {
      monthlyWeekOfMonthCtrl?.enable();
      monthlyDayOfMonthCtrl?.enable();
    }
  }

  onTabChange(index: number): void {
    this.schedulerForm.patchValue({
      scheduleType: index === 0 ? ScheduleType.ONE_TIME : ScheduleType.RECURRING,
    });
  }

  updateSummary(): void {
    const formValue = this.schedulerForm.value;
    if (formValue.scheduleType === ScheduleType.ONE_TIME) {
      this.summary = this.buildOneTimeSummary(formValue.schedule);
    }
    if (formValue.scheduleType === ScheduleType.RECURRING) {
      this.summary = this.buildRecurringSummary(formValue.schedule);
    }
  }

  buildOneTimeSummary(schedule: any): string {
    const date = schedule.date;
    const time = schedule.time;
    if (!date || !time) {
      return '';
    }
    let summary = '';
    summary = `Will run once on ${this.formatDate(date)} at ${this.formatTime(time)}.`;
    return summary;
  }

  buildRecurringSummary(schedule: any): string {
    const freq = schedule.recurrenceFrequency;
    const r = schedule.recurrence;

    if (!freq || !r) {
      return '';
    }

    let summary = '';

    switch (freq) {
      case RecurrenceFrequency.DAILY:
        summary = this.buildDailySummary(r);
        break;

      case RecurrenceFrequency.WEEKLY:
        summary = this.buildWeeklySummary(r);
        break;

      case RecurrenceFrequency.MONTHLY:
        summary = this.buildMonthlySummary(r);
        break;
    }

    return summary + this.buildEndSummary(r);
  }

  buildDailySummary(r: any): string {
    return `Runs every ${r.interval} day${r.interval > 1 ? 's' : ''}
      at ${this.formatTime(r.time)}
      starting ${this.formatDate(r.startDate)}`;
  }

  buildWeeklySummary(r: any): string {
    const days = this.formatDays(r.daysOfWeek);

    return `Runs every ${r.interval} week${r.interval > 1 ? 's' : ''}
      on ${days}
      at ${this.formatTime(r.time)}
      starting ${this.formatDate(r.startDate)}`;
  }

  buildMonthlySummary(r: any): string {
    let repeatPart = '';
    const other = 'other';

    if (r.repeatType === 'on-day' && r.dateOfMonth) {
      repeatPart = `on day ${r.dateOfMonth}`;
    }

    if (r.repeatType === 'on-the' && r.weekOfMonth && r.dayOfWeek) {
      repeatPart = `on the ${r.weekOfMonth} ${r.dayOfWeek}`;
    }

    return `Occurs ${repeatPart}
      of every ${r.interval == 2 ? other + ' ' : ''} ${r.interval > 2 ? r.interval + ' ' : ''}month${r.interval > 1 ? 's' : ''}
      at ${this.formatTime(r.time)}
      starting ${this.formatDate(r.startDate)}`;
  }

  buildEndSummary(r: any): string {
    if (r.endType === EndType.ON_DATE && r.endDate) {
      return ` and ends on ${this.formatDate(r.endDate)}`;
    }

    if (r.endType === EndType.AFTER_OCCURRENCES && r.occurrences) {
      return ` and ends after ${r.occurrences} occurrence${r.occurrences > 1 ? 's' : ''}`;
    }

    if (r.endType === EndType.NEVER) {
      return ` and never ends`;
    }
    return '';
  }

  formatDate(date: Date): string {
    return date == null || undefined
      ? ''
      : new Date(date).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
  }

  formatTime(time: Date): string {
    return time == null || undefined
      ? ''
      : new Date(time).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        });
  }

  formatDays(days: string[]): string {
    if (!days || days.length === 0) {
      return '';
    }

    if (days.length === 1) {
      return days[0];
    }

    return days.slice(0, -1).join(', ') + ' and ' + days[days.length - 1];
  }

  getQueryDropDown() {
    const orgId = this.userManagementService.getOrgId();
    this.apiService
      .query<GetQueryRecordsQuery, GetQueryRecordsQueryVariables>(GetQueryRecords, {
        orgId: orgId,
        filterDropdownValue: false,
      })
      .then((response) => {
        this.queryRows = response?.data?.GetQueryRecords as QueryRecord[];
        this.populateQueryData(this.queryRows);
      })
      .catch((error) => {
        this.nzMessageService.error('Failed to get Query dropdown data');
        console.error('Error fetching query dropdown data', error);
      });
  }

  populateQueryData(queryList: QueryRecord[]): void {
    this.selectQueryOptions = queryList.map((query) => ({
      name: query.name ? query.name : '',
      value: query.queryId,
    }));
  }
  selectDayOfWeek(day: any): void {
    const array = this.daysOfWeekArray;
    const currentValues: DayOfWeek[] = array.value;
    const index = array.controls.findIndex((ctrl) => ctrl.value === day);

    if (index > -1) {
      array.removeAt(index);
      return;
    }
    const isValid = this.isValidDaySelection(day, currentValues);
    if (!isValid) {
      this.nzMessageService.warning('Please maintain at least 48 hours gap between selected days.');
      return;
    }

    array.push(this.fb.control(day));
    array.markAsDirty();
    array.markAsTouched();
  }
  isSelected(value: any): boolean {
    return this.daysOfWeekArray.value.includes(value);
  }
  getFirstLetter(value: string): string {
    if (!value || value.length === 0) {
      return '';
    }
    return value[0];
  }

  createSchedule() {
    if (this.schedulerForm.invalid) {
      this.schedulerForm.markAllAsTouched();
      return;
    }
    const payload = this.buildGraphqlPayload(this.schedulerForm.value);
    this.sendToGraphqlCreateScheduleMutation(payload);
  }

  buildGraphqlPayload(formValue: any): CreateSchedulerRecordInput {
    const orgId = this.userManagementService.getOrgId();
    const userEmail = this.userManagementService.getEmail();

    const base: CreateSchedulerRecordInput = {
      name: formValue.schedulerName,
      queryId: formValue.selectedQuery,
      queryName: this.selectedQueryRecord.name || '',
      queryType: this.selectedQueryRecord.queryType as QueryType,
      timezone: formValue.selectedTimezone,
      type: formValue.scheduleType,
      createdBy: userEmail,
      orgId: orgId,
      status: SchedulerStatus.ACTIVE,
      schedulerDescription: this.summary,
    };

    if (formValue.scheduleType === ScheduleType.ONE_TIME) {
      const startAt = this.combineDateTime(formValue.schedule.date, formValue.schedule.time);
      base.type = ScheduleType.ONE_TIME;
      base.startAt = startAt?.toISOString();
      return base;
    }

    // RECURRING
    const schedule = formValue.schedule;
    const recurrence = schedule.recurrence;
    const startAt = this.combineDateTime(recurrence.startDate, recurrence.time);

    const recurringPayload: any = {
      frequency: schedule.recurrenceFrequency,
      interval: recurrence.interval,
    };

    // WEEKLY
    if (schedule.recurrenceFrequency === RecurrenceFrequency.WEEKLY) {
      recurringPayload.weekly = {
        days: recurrence?.daysOfWeek,
      };
    }

    // MONTHLY
    if (schedule.recurrenceFrequency === RecurrenceFrequency.MONTHLY) {
      const monthlyRecurrenceInput: MonthlyRecurrenceInput = { mode: MonthlyMode.DAY_OF_MONTH };
      if (recurrence.repeatType === 'on-day') {
        monthlyRecurrenceInput.dayOfMonth = recurrence?.dateOfMonth;
      }
      if (recurrence.repeatType === 'on-the') {
        monthlyRecurrenceInput.mode = MonthlyMode.WEEKDAY_OF_MONTH;
        monthlyRecurrenceInput.weekOfMonth = recurrence?.weekOfMonth;
        monthlyRecurrenceInput.dayOfWeek = recurrence?.dayOfWeek;
      }
      recurringPayload.monthly = monthlyRecurrenceInput;
    }

    const end: ScheduleEndInput = { type: EndType.NEVER };
    if (recurrence.endType !== EndType.NEVER) {
      end.type = recurrence.endType;
      if (recurrence.endType === EndType.ON_DATE) {
        const isSameDate =
          startAt &&
          recurrence?.endDate &&
          startAt.getFullYear() === recurrence?.endDate?.getFullYear() &&
          startAt.getMonth() === recurrence?.endDate?.getMonth() &&
          startAt.getDate() === recurrence?.endDate?.getDate();

        if (isSameDate) {
          end.endAt = new Date(startAt.getTime() + 2 * 60 * 1000).toISOString();
        } else {
          end.endAt = recurrence?.endDate?.toISOString();
        }
      }

      if (recurrence.endType === EndType.AFTER_OCCURRENCES) {
        end.maxOccurrences = recurrence?.occurrences;
      }
    }

    return {
      ...base,
      startAt: startAt?.toISOString(),
      recurrence: recurringPayload,
      end: end,
    };
  }

  combineDateTime(selectedDate: Date, selectedTime: Date): Date | null {
    let finalDateTime: Date | null = null;
    if (selectedDate && selectedTime) {
      finalDateTime = new Date(selectedDate);
      finalDateTime.setHours(selectedTime.getHours(), selectedTime.getMinutes(), selectedTime.getSeconds());
    }
    return finalDateTime;
  }

  sendToGraphqlCreateScheduleMutation(schedulerRecordInput: CreateSchedulerRecordInput): void {
    this.isLoading = true;
    this.apiService
      .query<CreateSchedulerRecordMutation, CreateSchedulerRecordMutationVariables>(createSchedulerRecord, {
        schedulerRecordInput: schedulerRecordInput,
      })
      .then((result: any) => {
        console.log(result);
        this.triggerOk();
        this.nzMessageService.success('Schedule Created Successfully');
      })
      .catch((error: any) => {
        this.nzMessageService.error('Failed to Create the Schedule');
        console.error('Error creating new schedule', error);
      })
      .finally(() => (this.isLoading = false));
  }

  triggerOk(): void {
    this.nzModalRef.triggerOk();
  }

  close(): void {
    this.nzModalRef.close(null);
  }

  getSelectedQueryData(value: string): void {
    const selectedQuery: QueryRecord = this.queryRows.find((item) => item.queryId === value) || ({} as QueryRecord);
    this.selectedQueryRecord = selectedQuery;
    this.queryName = selectedQuery.name;
    this.queryId = selectedQuery.queryId;
    this.queryRaw = selectedQuery.query;
    this.queryDescription = selectedQuery.description;
  }
  disabledPastDate = (current: Date): boolean => dayjs(current).diff(dayjs(this.today), 'day') < 0;

  disabledPastDateForEnds = (current: Date): boolean => {
    if (this.schedulerForm?.value?.schedule?.recurrence?.startDate !== null) {
      const startDate = this.schedulerForm?.value?.schedule?.recurrence?.startDate;
      return dayjs(current).diff(dayjs(startDate), 'day') < 0;
    }
    return dayjs(current).diff(dayjs(this.today), 'day') < 0;
  };

  private isValidDaySelection(newDay: DayOfWeek, selectedDays: DayOfWeek[]): boolean {
    const newIndex = this.dayIndexMap[newDay];

    return selectedDays.every((day) => {
      const existingIndex = this.dayIndexMap[day];
      const diff = Math.abs(existingIndex - newIndex);
      const circularDiff = 7 - diff;
      return diff >= 2 && circularDiff >= 2;
    });
  }

  isDisabled(day: DayOfWeek): boolean {
    const selectedDays: DayOfWeek[] = this.daysOfWeekArray.value;

    if (selectedDays.includes(day)) return false;

    return !this.isValidDaySelection(day, selectedDays);
  }
}
